import type { Key } from 'react';
import React, { ReactNode } from 'react';
import classNames from 'classnames';

import { Icon, IconType } from '@components/Icon';
import type { MadeColumnType } from '../hooks/useMakeColumns';
import type { RowHandlers, VirtualTableExpandable } from './types';
import { CHECKBOX_COLUMN_WIDTH, DEFAULT_COLUMN_WIDTH, EXPAND_COLUMN_WIDTH, SIZELESS_FLEX_GROW } from './constants';
import { TableRowSelection } from 'antd/es/table/interface';

function getColumnValue(record: any, column: MadeColumnType): any {
    const dataIndex = column.dataIndex;
    if (dataIndex == null) return undefined;
    if (typeof dataIndex === 'string' || typeof dataIndex === 'number') {
        return record[dataIndex];
    }
    return dataIndex.reduce((acc, key) => acc?.[key], record);
}

interface BodyRowProps {
    record: any;
    recordKey: string;
    index: number;
    rowSelection?: TableRowSelection<any>;
    measureElement: (node: Element | null) => void;
    displayColumns: MadeColumnType[];
    onRow?: (record: any) => RowHandlers;
    rowClassName?: string | ((record: any, index: number, indent: number) => string);
    expandable?: VirtualTableExpandable;
    expandedKeys: Key[];
    setExpandedKeys: (updater: Key[] | ((prev: Key[]) => Key[])) => void;
}

export function BodyRow({
    record,
    index,
    recordKey,
    measureElement,
    displayColumns,
    rowSelection,
    onRow,
    rowClassName,
    expandable,
    expandedKeys,
    setExpandedKeys,
}: BodyRowProps) {
    const selectedRowKeys = rowSelection?.selectedRowKeys;
    const isSelected = !!selectedRowKeys?.includes(recordKey);
    const isExpanded = !!expandable && expandedKeys.includes(recordKey);
    const canExpand = !!expandable && (expandable.rowExpandable ? expandable.rowExpandable(record) : true);
    const rowHandlers = onRow?.(record);
    const resolvedRowClassName =
        typeof rowClassName === 'function'
            ? rowClassName(record, index, 0)
            : (rowClassName ?? (onRow ? 'virtual_table_row virtual_table_row_clickable' : undefined));

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!expandable) return;
        const nextKeys = isExpanded ? expandedKeys.filter(k => k !== recordKey) : [...expandedKeys, recordKey];
        setExpandedKeys(nextKeys);
    };

    return (
        <div
            data-index={index}
            ref={measureElement}
            className="virtual_table_row_wrapper"
        >
            <div
                className={classNames(
                    'virtual_table_row',
                    resolvedRowClassName,
                    isSelected && 'virtual_table_row_selected',
                )}
                onClick={rowHandlers?.onClick}
                onDoubleClick={rowHandlers?.onDoubleClick}
                onMouseMove={rowHandlers?.onMouseMove}
                onMouseLeave={rowHandlers?.onMouseLeave}
            >
                {displayColumns.map((col, colIndex) => {
                    const key = `${col.key}${col.title}${colIndex}`;
                    const isExpandColumn = col.key === 'virtual-table-expand';
                    const isCheckbox = col.key === 'virtual-table-checkbox';
                    if (isExpandColumn) {
                        const expandWidth = col.width ?? EXPAND_COLUMN_WIDTH;
                        return (
                            <div
                                key={key}
                                className="virtual_table_cell virtual_table_cell_expand"
                                style={{
                                    width: expandWidth,
                                    minWidth: expandWidth,
                                    flexGrow: expandWidth,
                                }}
                                onClick={e => e.stopPropagation()}
                            >
                                {canExpand ? (
                                    expandable?.expandIcon ? (
                                        expandable.expandIcon({
                                            expanded: Boolean(isExpanded),
                                            onExpand: (_record, event) => handleExpandClick(event),
                                            record,
                                        })
                                    ) : (
                                        <span
                                            className="virtual_table_expand_icon"
                                            style={{
                                                transform: isExpanded ? 'none' : 'rotate(-90deg)',
                                            }}
                                            onClick={handleExpandClick}
                                            role="button"
                                            aria-expanded={isExpanded}
                                        >
                                            <Icon type={IconType.CHEVRON_DOWN} />
                                        </span>
                                    )
                                ) : null}
                            </div>
                        );
                    }
                    if (isCheckbox) {
                        return (
                            <div
                                key={key}
                                className="virtual_table_cell virtual_table_cell_checkbox"
                                style={{
                                    width: CHECKBOX_COLUMN_WIDTH,
                                    minWidth: CHECKBOX_COLUMN_WIDTH,
                                    flexGrow: CHECKBOX_COLUMN_WIDTH,
                                }}
                                onClick={e => e.stopPropagation()}
                            >
                                {(rowSelection?.renderCell?.(isSelected, record, index, null) as ReactNode) ?? null}
                            </div>
                        );
                    }
                    const value = getColumnValue(record, col);
                    const cellContent = col.render ? col.render(value, record, index) : value;
                    const isSortable = !!col.sorter;
                    const cellWidth = col.width ?? DEFAULT_COLUMN_WIDTH;
                    const flexGrow = col.width ?? SIZELESS_FLEX_GROW;
                    return (
                        <div
                            key={key}
                            className={classNames('virtual_table_cell', isSortable && 'virtual_table_cell_sortable')}
                            style={{
                                width: cellWidth,
                                minWidth: cellWidth,
                                flexGrow: flexGrow,
                            }}
                        >
                            {cellContent}
                        </div>
                    );
                })}
            </div>
            {expandable && isExpanded && expandable.expandedRowRender?.(record, index, 0, true) != null && (
                <div className="virtual_table_expanded_row">{expandable.expandedRowRender(record, index, 0, true)}</div>
            )}
        </div>
    );
}
